import { ExperimentProviderContext } from '@carvana/experiment';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  browserCookieIdExperiments,
  userIdExperiments
} from '~/constants/vdp/experiments';
import { useIsLoggedIn } from '~/context/common/UserContext';
import GlobalContext from '~/context/vdp/GlobalContext';
import { logError } from '~/utilities/loggers';

/**
 * Mark experiments as loaded once all are stored.
 * If experiments take > 10 seconds to load, produce an error but mark them as
 * loaded so the page can get on with its business.
 */
export default function ExperimentsLoaded() {
  const { experiments } = useContext(ExperimentProviderContext);
  const { setExperimentsLoaded } = useContext(GlobalContext);
  const isLoggedIn = useIsLoggedIn();
  const [forceLoad, setForceLoad] = useState(false);

  const experimentNames = useMemo(
    () =>
      Object.keys({
        ...browserCookieIdExperiments,
        ...(isLoggedIn ? userIdExperiments : {})
      }),
    [isLoggedIn]
  );

  const loadedExperiments = experimentNames.filter(
    experimentName => experiments[experimentName]?.assignedBucket != null
  );

  const didLoad =
    loadedExperiments.length === experimentNames.length || forceLoad;

  const unloadedExperiments = useMemo(
    () =>
      experimentNames.filter(
        experimentName => !loadedExperiments.includes(experimentName)
      ),
    [experimentNames, loadedExperiments]
  );

  useEffect(() => {
    const t = window.setTimeout(() => {
      logError(new Error('VDP global experiments failed to load'), {
        location: 'ExperimentsLoaded',
        unloadedExperiments
      });
      setForceLoad(true);
    }, 10000);

    if (didLoad) {
      window.clearTimeout(t);
      setExperimentsLoaded(true);
    }

    return () => window.clearTimeout(t);
  }, [didLoad, setExperimentsLoaded, unloadedExperiments]);

  return null;
}
